import { List, ListItem, ListItemAvatar, ListItemText, ListProps, Skeleton, SxProps } from '@mui/material'
import { skeletonFadeInSx } from 'c1ui/theme/C1Transitions'
import { APIError } from 'components/core/errors/api'
import { C1PagedListStyles } from 'components/core/list/C1List/C1PagedListStyles'
import { C1TablePagination } from 'components/core/pagination/C1TablePagination'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { FC, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { SerializedErrorType } from 'redux_toolkit/lib/reducer'

type GetId<T> = (row: T, i: number) => string
export type C1PagedListProps<T, ExtraProps> = {
    data: ReadonlyArray<ReadonlyArray<T>>
    isError?: boolean
    isLoading?: boolean
    isBackgroundLoading?: boolean
    error?: SerializedErrorType
    canLoadMore?: boolean
    emptyState?: React.ReactElement
    getId?: GetId<T>
    pageSize?: number
    totalRowCount?: number
    ListItemComponent: FC<{ row: T; extras?: ExtraProps; index: number }>
    ListItemSkeleton?: FC
    staticExtraProps?: ExtraProps
    usePageNumbers?: boolean
    hidePageLabel?: boolean
    page: number
    onPageChange?: (pageNumber: number) => void
    sx?: SxProps
} & ListProps

export const C1PagedList = <T, ExtraProps = object>({
    emptyState,
    getId = (x): string => (x as { id: string }).id,
    pageSize = 10,
    totalRowCount: _totalRowCount,
    ListItemComponent,
    ListItemSkeleton,
    staticExtraProps: _staticExtraProps = {} as ExtraProps,
    usePageNumbers: _usePageNumbers,
    hidePageLabel: _hidePageLabel,
    data,
    isError,
    isLoading,
    isBackgroundLoading,
    error,
    canLoadMore,
    page,
    onPageChange,
    sx,
}: C1PagedListProps< T, ExtraProps>): React.ReactNode => {
    const extraProps = useRef(_staticExtraProps).current

    if (isError) {
        return <APIError message={error?.message} code={error?.code} />
    }

    if (isLoading) {
        return (
            <List sx={sxc(C1PagedListStyles.container, sx)}>
                {[...Array(5)].map((_, i) => (
                    ListItemSkeleton
                        ? <ListItemSkeleton key={`list-skeleton-${i}`} />
                        : <ListItem key={`list-skeleton-${i}`} sx={skeletonFadeInSx}>
                            <ListItemAvatar>
                                <Skeleton variant="circular" width={24} height={24} />
                            </ListItemAvatar>
                            <ListItemText>
                                <Skeleton variant="text" />
                            </ListItemText>
                        </ListItem>))}
            </List>
        )
    }

    // Show empty state if no data
    if (!data || data.flat().length === 0) {
        return (
            <>
                {emptyState ? (
                    <>{emptyState}</>
                ) : (
                    <List>
                        <ListItemText>
                            <FormattedMessage
                                id="component_C1PagedList_no_items_found"
                                defaultMessage="There are no items."
                            />
                        </ListItemText>
                    </List>
                )}
            </>
        )
    }

    return <>
        <List sx={sxc(C1PagedListStyles.container, sx)}>
            {page + 1 <= data.length && data[page].map((row, i) => (
                <ListItemComponent row={row} key={getId(row, i)} extras={extraProps} index={i} />
            ))}
        </List>
        {onPageChange && (
            <C1TablePagination
                page={page}
                pageSize={pageSize}
                data={data}
                canLoadMore={canLoadMore}
                isLoading={isBackgroundLoading}
                onPageChange={onPageChange}
                totalCount={_totalRowCount}
                sx={C1PagedListStyles.pagination as SxProps}
            />
        )}
    </>
}
